import { useQuery } from '@tanstack/react-query';
import { fetcher } from '../../lib/api-fetcher';
import type { PaginatedResponse } from '@repo/types';

export interface DashboardKPIs {
  collectionRate: number;
  totalCollected: number;
  pendingAmount: number;
}

export interface TrialBalanceRow {
  accountCode: string;
  accountName: string;
  drBalance: number;
  crBalance: number;
}

export interface TrialBalanceReport {
  asOfDate: string;
  rows: TrialBalanceRow[];
  totalDr: number;
  totalCr: number;
  isBalanced: boolean;
}

export interface PNLStatement {
  income: Array<{ category: string; amount: number }>;
  expenses: Array<{ category: string; amount: number }>;
  netProfit: number;
}

export interface BalanceSheet {
  assets: Array<{ category: string; amount: number }>;
  liabilities: Array<{ category: string; amount: number }>;
  equity: Array<{ category: string; amount: number }>;
  totalAssets: number;
  totalLiabilitiesAndEquity: number;
  isBalanced: boolean;
}

export interface AgentPerformance {
  agentId: string;
  agentName: string;
  assignedCount: number;
  collectedCount: number;
  collectedAmount: number;
  pendingAmount: number;
  defaultRate: number;
  avgDaysToCollect: number;
  eodSubmissionRate: number;
}

export interface AuthLogEntry {
  id: string;
  eventType: string;
  actorId: string | null;
  occurredAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  payload: Record<string, unknown>;
}

export function unwrapData<T>(res: unknown): T {
  if (res && typeof res === 'object' && 'data' in res) {
    return (res as { data: T }).data;
  }
  return res as T;
}

export function useStaffDashboardKPIs() {
  return useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async (): Promise<DashboardKPIs> => {
      const res = await fetcher.get<DashboardKPIs | { data: DashboardKPIs }>('/staff/v1/reporting/dashboard-kpis');
      return unwrapData<DashboardKPIs>(res);
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useTrialBalance(asOfDate?: string) {
  return useQuery({
    queryKey: ['reports', 'trial-balance', asOfDate],
    queryFn: async (): Promise<TrialBalanceReport> => {
      const params = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      const res = await fetcher.get<TrialBalanceReport | { data: TrialBalanceReport }>(`/staff/v1/reports/trial-balance${params}`);
      return unwrapData<TrialBalanceReport>(res);
    },
    enabled: !!asOfDate,
  });
}

export function usePNLStatement(fromDate?: string, toDate?: string, comparativeFrom?: string, comparativeTo?: string) {
  return useQuery({
    queryKey: ['reports', 'pnl', fromDate, toDate, comparativeFrom, comparativeTo],
    queryFn: async (): Promise<PNLStatement> => {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      if (comparativeFrom) params.append('comparativeFrom', comparativeFrom);
      if (comparativeTo) params.append('comparativeTo', comparativeTo);
      const qs = params.toString();
      const res = await fetcher.get<PNLStatement | { data: PNLStatement }>(`/staff/v1/reporting/pnl${qs ? `?${qs}` : ''}`);
      return unwrapData<PNLStatement>(res);
    },
  });
}

export function useBalanceSheet(asOfDate?: string) {
  return useQuery({
    queryKey: ['reports', 'balance-sheet', asOfDate],
    queryFn: async (): Promise<BalanceSheet> => {
      const params = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
      const res = await fetcher.get<BalanceSheet | { data: BalanceSheet }>(`/staff/v1/reporting/balance-sheet${params}`);
      return unwrapData<BalanceSheet>(res);
    },
  });
}

export interface StaffActivityReportEntry {
  id: string;
  userId: string;
  userName: string;
  action: string;
  details?: string;
  createdAt: string;
}

export interface ArrearsReportEntry {
  id: string;
  customerName: string;
  groupCode: string;
  ticketNumber: string;
  overdueInstallments: number;
  unpaidPaise: number;
  interestFinesPaise: number;
  lastPaymentDate: string;
  agingBracket: '30_DAYS' | '60_DAYS' | '90_PLUS_DAYS';
}

export interface NotificationLogEntry {
  id: string;
  createdAt?: string;
  sentAt?: string;
  recipientMobile?: string | null;
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  recipientId?: string | null;
  channel: string;
  status: string;
  templateKey?: string;
  templateId?: string;
  renderedBody?: string | null;
}

export function useCollectionPerformance(dateRange?: Record<string, string>) {
  return useQuery({
    queryKey: ['reports', 'collection-performance', dateRange],
    queryFn: async (): Promise<AgentPerformance[]> => {
      const params = dateRange ? '?' + new URLSearchParams(dateRange).toString() : '';
      const res = await fetcher.get<AgentPerformance[] | { data: AgentPerformance[] }>(`/staff/v1/reporting/collection-performance${params}`);
      return unwrapData<AgentPerformance[]>(res) || [];
    },
  });
}

export function useStaffActivityReport(dateRange?: Record<string, string>) {
  return useQuery({
    queryKey: ['reports', 'staff-activity', dateRange],
    queryFn: async (): Promise<StaffActivityReportEntry[]> => {
      const params = dateRange ? '?' + new URLSearchParams(dateRange).toString() : '';
      const res = await fetcher.get<StaffActivityReportEntry[] | { data: StaffActivityReportEntry[] }>(`/staff/v1/reporting/staff-activity${params}`);
      return unwrapData<StaffActivityReportEntry[]>(res) || [];
    },
  });
}

export interface QueuedPdfJob {
  jobId: string;
  status: 'QUEUED';
}

export interface PdfJobStatus {
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  result?: { storageRef?: string };
  error?: string;
}

/**
 * P0-003: compliance PDF endpoints are async — they return 202 + jobId and the
 * client polls this shared status route until BullMQ finishes rendering.
 */
export async function pollPdfJobStatus(jobId: string): Promise<PdfJobStatus> {
  for (let i = 0; i < 60; i++) {
    const res = await fetcher.get<PdfJobStatus | { data: PdfJobStatus }>(`/pdf/status/${jobId}`);
    const status = unwrapData<PdfJobStatus>(res);
    if (status.status === 'COMPLETED' || status.status === 'FAILED') {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  throw new Error(`PDF job ${jobId} did not complete within 60s`);
}

export function useSubscriberRegister(groupId: string) {
  return useQuery({
    queryKey: ['reports', 'subscriber-register', groupId],
    queryFn: async (): Promise<{ storageRef: string }> => {
      const queuedRes = await fetcher.get<QueuedPdfJob | { data: QueuedPdfJob }>(`/staff/v1/compliance/schemes/${groupId}/subscriber-register`);
      const queued = unwrapData<QueuedPdfJob>(queuedRes);
      const status = await pollPdfJobStatus(queued.jobId);
      if (status.status !== 'COMPLETED' || !status.result?.storageRef) {
        throw new Error(status.error ?? 'Subscriber register PDF generation failed');
      }
      return { storageRef: status.result.storageRef };
    },
    enabled: !!groupId,
  });
}

export interface DaybookEntry {
  id: string;
  voucherNumber: string;
  accountName: string;
  entryType: 'RECEIPT' | 'PAYMENT';
  amountPaise: number;
  narration: string;
  reference: string;
}

export function useDaybook(date: string) {
  return useQuery({
    queryKey: ['reports', 'daybook', date],
    queryFn: async () => {
      const params = new URLSearchParams({ fromDate: date, toDate: date });
      const res = await fetcher.get<DaybookEntry[] | { data: DaybookEntry[] }>(`/staff/v1/reporting/daybook?${params.toString()}`);
      return unwrapData<DaybookEntry[]>(res) || [];
    },
  });
}

export function useArrearsReport(filters?: Record<string, string | undefined>) {
  return useQuery({
    queryKey: ['reports', 'arrears', filters],
    queryFn: async () => {
      const cleanFilters: Record<string, string> = {};
      if (filters) {
        for (const [k, v] of Object.entries(filters)) {
          if (v !== undefined) cleanFilters[k] = v;
        }
      }
      const params = Object.keys(cleanFilters).length ? '?' + new URLSearchParams(cleanFilters).toString() : '';
      const res = await fetcher.get<ArrearsReportEntry[] | { data: ArrearsReportEntry[] }>(`/staff/v1/reporting/arrears${params}`);
      return unwrapData<ArrearsReportEntry[]>(res) || [];
    },
  });
}

export function useAuthLogs(filters?: { status?: string; from?: string; to?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['logs', 'auth', filters],
    queryFn: async (): Promise<PaginatedResponse<AuthLogEntry>> => {
      const params = filters ? '?' + new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<AuthLogEntry>>(`/staff/v1/auth-logs${params}`);
    },
  });
}

export function useNotificationLogs(filters?: Record<string, string | number | undefined>) {
  return useQuery({
    queryKey: ['logs', 'notifications', filters],
    queryFn: async (): Promise<PaginatedResponse<NotificationLogEntry>> => {
      const cleanFilters: Record<string, string> = {};
      if (filters) {
        for (const [k, v] of Object.entries(filters)) {
          if (v !== undefined) cleanFilters[k] = String(v);
        }
      }
      const params = Object.keys(cleanFilters).length ? '?' + new URLSearchParams(cleanFilters).toString() : '';
      return fetcher.get<PaginatedResponse<NotificationLogEntry>>(`/staff/v1/notification-log${params}`);
    },
  });
}